// src/components/admin/AdminAccueil.jsx — Tableau de bord admin

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, CalendarDays, Ticket, Wallet, TrendingUp, Inbox, AlertTriangle } from "lucide-react";
import { getStatsAdmin } from "../../services/AdminGestionService";
import { CarteKpi, EnTete, BoutonActualiser, euros } from "./AdminUI";

const Courbe = ({ points }) => {
    const max = Math.max(1, ...points.map((p) => p.nb));
    return (
        <div className="flex items-end gap-1 h-32">
            {points.map((p) => (
                <div key={p.jour} className="flex-1 group relative">
                    <div className="w-full rounded-t bg-violet-500/80 group-hover:bg-violet-600 transition"
                        style={{ height: `${Math.max(2, (p.nb / max) * 120)}px` }} />
                    <div className="hidden group-hover:block absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                        {new Date(p.jour).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} : {p.nb}
                    </div>
                </div>
            ))}
        </div>
    );
};

const Ligne = ({ label, valeur, fort }) => (
    <div className="flex justify-between py-1.5 text-sm border-b border-gray-50 last:border-0">
        <span className="text-gray-500">{label}</span>
        <span className={fort ? "font-extrabold text-gray-900" : "font-semibold text-gray-700"}>{valeur}</span>
    </div>
);

const AdminAccueil = () => {
    const navigate = useNavigate();
    const [s, setS] = useState(null);
    const [erreur, setErreur] = useState(null);

    const charger = () => getStatsAdmin().then(setS).catch((e) => setErreur(e.message));
    useEffect(() => { charger(); }, []);

    if (erreur) return <p className="text-red-500">{erreur}</p>;
    if (!s) return <p className="text-gray-400">Chargement…</p>;

    const f = s.finances;

    return (
        <div className="max-w-7xl mx-auto">
            <EnTete titre="Vue d'ensemble" sousTitre="Toute l'activité de Thrundrz" actions={<BoutonActualiser onClick={charger} />} />

            {(s.demandes_en_attente > 0 || f.versements_echec > 0 || f.a_synchroniser > 0) && (
                <div className="space-y-2 mb-5">
                    {s.demandes_en_attente > 0 && (
                        <button onClick={() => navigate("/admin/demandes")}
                            className="w-full flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-semibold text-left">
                            <Inbox size={16} /> {s.demandes_en_attente} demande(s) d'organisateur à traiter
                        </button>
                    )}
                    {f.versements_echec > 0 && (
                        <button onClick={() => navigate("/admin/versements")}
                            className="w-full flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-sm font-semibold text-left">
                            <AlertTriangle size={16} /> {f.versements_echec} versement(s) en échec
                        </button>
                    )}
                    {f.a_synchroniser > 0 && (
                        <button onClick={() => navigate("/admin/finances")}
                            className="w-full flex items-center gap-2 px-4 py-3 rounded-xl bg-blue-50 border border-blue-200 text-blue-700 text-sm font-semibold text-left">
                            <Wallet size={16} /> {f.a_synchroniser} paiement(s) à synchroniser avec Stripe
                        </button>
                    )}
                </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <CarteKpi label="Chiffre d'affaires" valeur={euros(f.chiffre_affaires)} sous="payé par les acheteurs" icone={Wallet} />
                <CarteKpi label="Bénéfice net Thrundrz" valeur={euros(f.benefice_net)} couleur="text-green-600" sous="après Stripe et annulations" icone={TrendingUp} />
                <CarteKpi label="Réservations" valeur={s.reservations.total} sous={`${s.reservations["30j"]} sur 30 jours`} icone={Ticket} />
                <CarteKpi label="Utilisateurs" valeur={s.utilisateurs.total} sous={`+${s.utilisateurs.nouveaux_30j} sur 30 jours`} icone={Users} />
            </div>

            <div className="grid lg:grid-cols-3 gap-4 mb-6">
                <section className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-4">Réservations — 30 derniers jours</h3>
                    <Courbe points={s.courbe} />
                </section>

                <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-3">Argent</h3>
                    <Ligne label="Encaissé (acheteurs)" valeur={euros(f.chiffre_affaires)} />
                    <Ligne label="Part des organisateurs" valeur={euros(f.part_organisateurs)} />
                    <Ligne label="Frais Stripe" valeur={`− ${euros(f.frais_stripe)}`} />
                    <Ligne label="Commission Thrundrz" valeur={euros(f.commission)} />
                    <Ligne label="Coût des annulations" valeur={`− ${euros(f.cout_annulations)}`} />
                    <Ligne label="Bénéfice net" valeur={euros(f.benefice_net)} fort />
                    <Ligne label="À verser aux organisateurs" valeur={euros(f.a_verser)} />
                </section>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
                <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><Users size={15} /> Utilisateurs</h3>
                    {Object.entries(s.utilisateurs.par_role).map(([role, nb]) => (
                        <Ligne key={role} label={role || "sans rôle"} valeur={nb} />
                    ))}
                    <Ligne label="Bloqués" valeur={s.utilisateurs.bloques} />
                </section>
                <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><CalendarDays size={15} /> Événements</h3>
                    <Ligne label="Total" valeur={s.evenements.total} fort />
                    <Ligne label="À venir" valeur={s.evenements.a_venir} />
                    <Ligne label="Billetterie Thrundrz" valeur={s.evenements.thrundrz} />
                    <Ligne label="Billetterie externe" valeur={s.evenements.externe} />
                    <Ligne label="Désactivés" valeur={s.evenements.inactifs} />
                    <Ligne label="Annulés" valeur={s.evenements.annules} />
                </section>
                <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><Ticket size={15} /> Réservations</h3>
                    <Ligne label="Total" valeur={s.reservations.total} fort />
                    <Ligne label="Payantes" valeur={s.reservations.payantes} />
                    <Ligne label="Gratuites" valeur={s.reservations.gratuites} />
                    <Ligne label="Annulées" valeur={s.reservations.annulees} />
                </section>
            </div>
        </div>
    );
};

export default AdminAccueil;